<template>
  <div
    class="w-11 h-[54px] md:w-12 md:h-[60px] lg:w-16 lg:h-20 xl:w-24 xl:h-30 rounded-[4px] lg:rounded-lg flex items-center justify-center transition-all duration-300"
    :class="[
      isEmpty ? 'bg-cw-grey' : 'bg-cw-green-alt',
      isDragOver && !isEmpty ? 'ring-4 ring-blue-400 ring-opacity-50 scale-175 bg-blue-100 shadow-2xl' : '',
      !isEmpty && acceptDrops ? 'hover:ring-2 hover:ring-blue-300 hover:ring-opacity-30' : ''
    ]"
    @dragover.prevent="handleDragOver"
    @dragenter.prevent="handleDragEnter"
    @dragleave="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { CardDropEventT, CardT } from '@/types/Game'

const { isEmpty = false, acceptDrops = false } = defineProps<{
  isEmpty?: boolean
  acceptDrops?: boolean
}>()

const emit = defineEmits<{
  cardDropped: [card: Partial<CardT>]
}>()

const isDragOver = ref(false)

const handleDragOver = (event: DragEvent) => {
  if (!acceptDrops || isEmpty) return
  event.preventDefault()
}

const handleDragEnter = (event: DragEvent) => {
  if (!acceptDrops || isEmpty) return
  event.preventDefault()
  isDragOver.value = true
}

const handleDragLeave = (event: DragEvent) => {
  if (!acceptDrops || isEmpty) return

  // Only set isDragOver to false if we're actually leaving the element
  // and not just moving to a child element
  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  const isInsideElement = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom

  if (!isInsideElement) {
    isDragOver.value = false
  }
}

const handleDrop = (event: DragEvent) => {
  if (!acceptDrops || isEmpty) return

  event.preventDefault()
  isDragOver.value = false

  try {
    const cardData = event.dataTransfer?.getData('application/json')
    if (cardData) {
      const card = JSON.parse(cardData) as CardDropEventT
      emit('cardDropped', card.card)
    }
  } catch (error) {
    console.error('Failed to parse dropped card data:', error)
  }
}
</script>
